import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { Efetivo } from '@core/models/models';
import { AuthService } from '@auth/services/auth.service';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import { SkeletonModule } from 'primeng/skeleton';

interface MenuPopup {
  toggle(event: Event): void;
}

@Component({
  selector: 'app-lista-efetivos',
  standalone: true,
  imports: [CommonModule, PaginatorControlComponent, SkeletonModule, MenuModule],
  templateUrl: './lista-efetivos.component.html',
  styleUrl: './lista-efetivos.component.scss',
})
export class ListaEfetivosComponent implements OnInit, OnChanges {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  @Input() Efetivos: Efetivo[] = [];
  @Input() pagination: any;
  @Input() fotoPreviewMap: {
    [id: number]: string | ArrayBuffer | null;
  } = {};
  @Input() skeletonLoading = false;

  @Output() toggleEstado = new EventEmitter<Efetivo>();
  @Output() emitirPasse = new EventEmitter<Efetivo>();
  @Output() enviarNotificacao = new EventEmitter<Efetivo>();
  @Output() deleteEfetivo = new EventEmitter<Efetivo>();
  @Output() verPerfil = new EventEmitter<Efetivo>();
  @Output() editar = new EventEmitter<Efetivo>();
  @Output() suspender = new EventEmitter<Efetivo>();
  @Output() afastar = new EventEmitter<Efetivo>();
  @Output() reativar = new EventEmitter<Efetivo>();
  @Output() pageChange = new EventEmitter<any>();
  @Output() filterChange = new EventEmitter<any>();

  readonly skeletonRows = [1, 2, 3, 4, 5];
  readonly filterForm = new FormGroup({});

  showData = false;
  activeMenuEfetivo: Efetivo | null = null;

  menuItems: MenuItem[] = [];

  private buildMenuItems(efetivo: Efetivo): void {
    const estado = this.getEstadoEfetivo(efetivo);
    const items: MenuItem[] = [
      {
        label: 'Ver Ficha',
        icon: 'pi pi-eye',
        command: () => {
          if (this.activeMenuEfetivo) {
            this.verPerfil.emit(this.activeMenuEfetivo);
          }
        },
      },
    ];

    if (estado === 4) {
      this.menuItems = items;
      return;
    }

    if (estado === 5) {
      items.splice(1, 0, this.createEditAction());
      items.push(
        { separator: true },
        this.createStateAction('Desativar', 'pi pi-pause-circle', () =>
          this.toggleEstado.emit(this.activeMenuEfetivo!),
        ),
        { separator: true },
        this.createDeleteAction(),
      );
      this.menuItems = items;
      return;
    }

    if (estado === 0 || estado === 2 || estado === 3) {
      items.splice(1, 0, this.createEditAction());
    }

    switch (estado) {
      case 0:
        items.push(
          { separator: true },
          this.createStateAction('Suspender', 'pi pi-pause-circle', () =>
            this.suspender.emit(this.activeMenuEfetivo!),
          ),
          this.createStateAction('Afastar', 'pi pi-user-minus', () =>
            this.afastar.emit(this.activeMenuEfetivo!),
          ),
          this.createStateAction('Desativar', 'pi pi-ban', () =>
            this.toggleEstado.emit(this.activeMenuEfetivo!),
          ),
        );
        break;
      case 2:
        items.push(
          { separator: true },
          this.createStateAction('Reativar', 'pi pi-play-circle', () =>
            this.reativar.emit(this.activeMenuEfetivo!),
          ),
        );
        break;
      case 3:
        items.push(
          { separator: true },
          this.createStateAction('Reativar', 'pi pi-play-circle', () =>
            this.reativar.emit(this.activeMenuEfetivo!),
          ),
          this.createStateAction('Desativar', 'pi pi-ban', () =>
            this.toggleEstado.emit(this.activeMenuEfetivo!),
          ),
        );
        break;
      case 1:
        if (this.isAdministrador()) {
          items.push(
            { separator: true },
            this.createStateAction('Reativar', 'pi pi-play-circle', () =>
              this.reativar.emit(this.activeMenuEfetivo!),
            ),
            { separator: true },
            this.createDeleteAction(),
          );
        }
        break;
    }

    this.menuItems = items;
  }

  private createEditAction(): MenuItem {
    return {
      label: 'Editar',
      icon: 'pi pi-pencil',
      command: () => {
        if (this.activeMenuEfetivo) this.editar.emit(this.activeMenuEfetivo);
      },
    };
  }

  private createStateAction(
    label: string,
    icon: string,
    command: () => void,
  ): MenuItem {
    return { label, icon, command };
  }

  private createDeleteAction(): MenuItem {
    return {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      styleClass: 'menu-item-danger',
      command: () => {
        if (this.activeMenuEfetivo) this.deleteEfetivo.emit(this.activeMenuEfetivo);
      },
    };
  }

  private isAdministrador(): boolean {
    return this.auth.getGrupos().some(
      (grupo) => grupo.trim().toLocaleLowerCase() === 'administrador',
    );
  }

  private getEstadoEfetivo(efetivo: Efetivo): number | null {
    const rawEstado =
      efetivo.estadoEfetivo !== undefined && efetivo.estadoEfetivo !== null
        ? efetivo.estadoEfetivo
        : efetivo.estado;

    if (rawEstado === undefined || rawEstado === null || rawEstado === '') {
      return null;
    }

    const estadoNumerico = Number(rawEstado);
    if (!Number.isNaN(estadoNumerico)) return estadoNumerico;

    const estados: Record<string, number> = {
      ATIVO: 0,
      ACTIVO: 0,
      INATIVO: 1,
      INACTIVO: 1,
      AFASTADO: 2,
      SUSPENSO: 3,
      'DE FOLGA': 4,
      DE_FOLGA: 4,
      'FÉRIAS': 5,
      FERIAS: 5,
    };

    return estados[String(rawEstado).trim().toUpperCase()] ?? null;
  }

  ngOnInit(): void {
    this.updateShowData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pagination'] || changes['skeletonLoading']) {
      this.updateShowData();
    }
  }

  openActions(event: Event, efetivo: Efetivo, menu: MenuPopup): void {
    event.preventDefault();
    event.stopPropagation();
    this.activeMenuEfetivo = efetivo;
    this.buildMenuItems(efetivo);
    menu.toggle(event);
  }

  callRouteDetailsEfetivo(data: Efetivo): void {
    const id = data?.id;
    if (id) this.router.navigate(['/admin/efetivos', id]);
  }

  editarEfetivo(data: Efetivo): void {
    const id = data?.id;
    if (id) this.router.navigate(['/admin/efetivos', id, 'editar']);
  }

  onPageChange(event: any): void {
    this.activeMenuEfetivo = null;
    this.pageChange.emit(event);
  }

  onFilterChange(filterValues: any): void {
    const cleaned = Object.entries(filterValues || {}).reduce(
      (acc, [key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          value !== '' &&
          value !== 0
        ) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>,
    );

    if (cleaned['ordem']) {
      switch (String(cleaned['ordem'])) {
        case 'nome-az':
          cleaned['OrderBy'] = 'nome asc';
          break;
        case 'nome-za':
          cleaned['OrderBy'] = 'nome desc';
          break;
        case 'registo':
          cleaned['OrderBy'] = 'id desc';
          break;
      }
      delete cleaned['ordem'];
    }

    this.filterChange.emit(cleaned);
  }

  getAvatarColor(id: number): string {
    const colors = ['green', 'blue', 'orange', 'purple', 'teal', 'red'];
    return colors[id % colors.length];
  }

  getInitials(nome: string): string {
    if (!nome) return '?';

    const parts = nome.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  getTurnoBadge(turno: string | null | undefined): {
    classe: string;
    icon: string;
    label: string;
  } {
    switch ((turno || '').toLowerCase()) {
      case 'diurno':
        return {
          classe: 'blue',
          icon: 'pi pi-sun',
          label: 'Diurno',
        };
      case 'noturno':
        return {
          classe: 'navy',
          icon: 'pi pi-moon',
          label: 'Noturno',
        };
      case 'rotativo':
        return {
          classe: 'green',
          icon: 'pi pi-sync',
          label: 'Rotativo',
        };
      default:
        return { classe: 'grey', icon: '', label: '—' };
    }
  }

  getEstadoBadge(estado: number | string | null | undefined): {
    classe: string;
    icon: string;
    label: string;
  } {
    let numericState: number | null = null;
    if (estado !== null && estado !== undefined && estado !== '') {
      numericState = Number(estado);
      if (isNaN(numericState)) {
        const stateMap: Record<string, number> = {
          'Ativo': 0, 'ATIVO': 0, 'ACTIVO': 0,
          'Inativo': 1, 'INATIVO': 1, 'INACTIVO': 1,
          'Afastado': 2, 'AFASTADO': 2,
          'Suspenso': 3, 'SUSPENSO': 3, 'Suspensos': 3,
          'De Folga': 4, 'DE FOLGA': 4, 'De_Folga': 4,
          'Férias': 5, 'FERIAS': 5
        };
        numericState = stateMap[String(estado).trim()] ?? null;
      }
    }

    if (numericState === null) {
      return {
        classe: 'grey',
        icon: 'pi pi-minus',
        label: '—',
      };
    }

    switch (numericState) {
      case 0: // Ativo
        return {
          classe: 'green',
          icon: 'pi pi-check-circle',
          label: 'Ativo',
        };
      case 1: // Inativo
        return {
          classe: 'red',
          icon: 'pi pi-ban',
          label: 'Inativo',
        };
      case 2: // Afastado
        return {
          classe: 'orange',
          icon: 'pi pi-minus-circle',
          label: 'Afastado',
        };
      case 3: // Suspenso
        return {
          classe: 'red',
          icon: 'pi pi-exclamation-circle',
          label: 'Suspenso',
        };
      case 4: // De Folga
        return {
          classe: 'blue',
          icon: 'pi pi-clock',
          label: 'De Folga',
        };
      case 5: // Férias
        return {
          classe: 'yellow',
          icon: 'pi pi-calendar',
          label: 'Férias',
        };
      default:
        return {
          classe: 'grey',
          icon: 'pi pi-minus',
          label: '—',
        };
    }
  }

  private updateShowData(): void {
    const hasData = (this.pagination?.data?.length ?? 0) > 0;
    this.showData = this.skeletonLoading || hasData;
  }
}
